import { type Component, For, Show } from "solid-js";
import { CheckCircle2, XCircle, User, Sparkles, BadgeCheck, HelpCircle } from "lucide-solid";
import type { UiChatMessage } from "./chat-store";

interface Props {
  message: UiChatMessage;
}

export const ChatMessage: Component<Props> = (props) => {
  const isUser = () => props.message.role === "user";
  const isAssistant = () => props.message.role === "assistant";
  const isTool = () => props.message.role === "tool";
  const isSummary = () => isAssistant() && props.message.summary === true;
  const isAskQuestion = () => isAssistant() && props.message.ask_question === true;

  // Skip displaying tool result messages — chỉ display assistant + user
  // (tool kết quả đã được summary bằng AI ở turn sau).
  // Optional: nếu muốn hiện debug tool result, bỏ guard này.
  if (isTool()) return null;

  return (
    <div
      class="flex gap-2"
      classList={{ "justify-end": isUser(), "justify-start": !isUser() }}
    >
      <Show when={isAssistant()}>
        <div class="flex-shrink-0 mt-1">
          <Show
            when={isSummary()}
            fallback={
              <Show
                when={isAskQuestion()}
                fallback={<Sparkles class="h-5 w-5 text-blue-600" />}
              >
                <HelpCircle class="h-5 w-5 text-amber-600" />
              </Show>
            }
          >
            <BadgeCheck class="h-5 w-5 text-emerald-600" />
          </Show>
        </div>
      </Show>
      <div
        class="max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words"
        classList={{
          "bg-blue-600 text-white": isUser(),
          "bg-blue-50 dark:bg-blue-950/40 text-foreground border border-blue-200/40":
            isAssistant() && !isSummary() && !isAskQuestion(),
          "bg-emerald-50 dark:bg-emerald-950/40 text-foreground border border-emerald-300/50 ring-1 ring-emerald-500/20":
            isSummary(),
          "bg-amber-50 dark:bg-amber-950/40 text-foreground border border-amber-300/50 ring-1 ring-amber-500/20":
            isAskQuestion(),
        }}
      >
        <Show when={isSummary()}>
          <div class="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-1.5">
            <BadgeCheck class="h-3.5 w-3.5" />
            Done
          </div>
        </Show>
        <Show when={isAskQuestion()}>
          <div class="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1.5">
            <HelpCircle class="h-3.5 w-3.5" />
            AI hỏi
          </div>
        </Show>
        <div>{props.message.content}</div>
        <Show when={props.message.tool_trace && props.message.tool_trace.length > 0}>
          <div class="mt-2 space-y-1 pt-2 border-t border-current/10">
            <For each={props.message.tool_trace}>
              {(tc) => (
                <div class="flex items-center gap-1.5 text-[11px] font-mono">
                  <Show when={tc.ok} fallback={<XCircle class="h-3 w-3 text-destructive" />}>
                    <CheckCircle2 class="h-3 w-3 text-green-600" />
                  </Show>
                  <span class="opacity-80">{tc.name}</span>
                  <Show when={tc.error}>
                    <span class="text-destructive truncate">— {tc.error}</span>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>
      <Show when={isUser()}>
        <div class="flex-shrink-0 mt-1">
          <User class="h-5 w-5 text-muted-foreground" />
        </div>
      </Show>
    </div>
  );
};
